import { useEffect, useRef, useState } from "react";
import { API_BASE } from "../../lib/api";

export const CATEGORY_LABELS = {
  intraoral_photos: "Intraoral Photos",
  extraoral_photos: "Extraoral Photos",
  occlusal: "Occlusal",
  xrays: "X-rays",
  scans: "Scans",
  documents: "Documents",
  other: "Other",
};

export const CATEGORY_ORDER = [
  "intraoral_photos",
  "extraoral_photos",
  "occlusal",
  "xrays",
  "scans",
  "documents",
  "other",
];

export const PHOTO_CATEGORIES = ["intraoral_photos", "extraoral_photos", "occlusal"];

export const CATEGORY_ACCEPT = {
  intraoral_photos: "image/*",
  extraoral_photos: "image/*",
  occlusal: "image/*",
  xrays: "image/*,.pdf,.dcm",
  scans: "image/*,.pdf",
  documents: ".pdf,.txt,image/*",
  other: "image/*,.pdf,.txt",
};

export const isImageRecord = (rec) => (rec?.content_type || "").startsWith("image/");
export const isProgressPhoto = (rec) => isImageRecord(rec) && PHOTO_CATEGORIES.includes(rec?.category);
export const recordUrl = (rec) => `${API_BASE}/records/${rec.record_id}/download`;
export const shortDate = (d) => (d ? new Date(d).toLocaleDateString("en", { month: "short", day: "numeric", year: "numeric" }) : "—");
export const recordDate = (rec) => rec.capture_date || rec.created_at;
export const stageLabel = (rec) => (rec?.treatment_stage ? `Stage ${rec.treatment_stage}` : "Unstaged");

const stageNumber = (rec) => {
  const value = Number(rec?.treatment_stage || 0);
  return Number.isFinite(value) && value > 0 ? value : 9999;
};

const dateNumber = (rec) => {
  const time = new Date(recordDate(rec) || 0).getTime();
  return Number.isFinite(time) ? time : 0;
};

export const sortPhotoRecords = (records) =>
  [...records].sort((a, b) => stageNumber(a) - stageNumber(b) || dateNumber(a) - dateNumber(b));

export function groupPhotosByStage(records) {
  const groups = new Map();
  sortPhotoRecords(records).forEach((rec) => {
    const key = rec.treatment_stage || "unstaged";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(rec);
  });
  return Array.from(groups.entries());
}

export function PhotoCompareSlider({ before, after }) {
  const [position, setPosition] = useState(50);
  const frameRef = useRef(null);

  useEffect(() => {
    setPosition(50);
  }, [before?.record_id, after?.record_id]);

  if (!before || !after) return null;

  const setFromPointer = (clientX) => {
    const rect = frameRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const next = ((clientX - rect.left) / rect.width) * 100;
    setPosition(Math.min(96, Math.max(4, Math.round(next))));
  };

  const onKeyDown = (event) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      setPosition((value) => Math.max(4, value - 4));
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      setPosition((value) => Math.min(96, value + 4));
    }
  };

  return (
    <div data-testid="photo-compare-slider" className="space-y-3">
      <div
        ref={frameRef}
        role="slider"
        tabIndex={0}
        aria-label="Compare progress photos"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={position}
        data-testid="photo-compare-frame"
        onKeyDown={onKeyDown}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          setFromPointer(event.clientX);
        }}
        onPointerMove={(event) => {
          if (event.buttons === 1) setFromPointer(event.clientX);
        }}
        className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 cursor-ew-resize select-none outline-none transition-[box-shadow,border-color] duration-200 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20"
      >
        <img
          src={recordUrl(after)}
          alt={after.file_name}
          draggable="false"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <img
          src={recordUrl(before)}
          alt={before.file_name}
          draggable="false"
          className="absolute inset-0 h-full w-full object-cover"
          style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
        />
        <div className="absolute inset-y-0 w-0.5 bg-white shadow-[0_0_16px_rgba(15,23,42,0.45)]" style={{ left: `${position}%` }} />
        <div
          className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/95 border border-slate-200 shadow-[0_8px_20px_rgba(15,23,42,0.25)] flex items-center justify-center text-[10px] font-bold text-[#2563EB]"
          style={{ left: `${position}%` }}
        >
          ↔
        </div>
        <span className="absolute left-3 top-3 rounded-full bg-slate-900/70 px-3 py-1 text-[10px] font-bold tracking-[0.12em] uppercase text-white">
          {stageLabel(before)}
        </span>
        <span className="absolute right-3 top-3 rounded-full bg-[#2563EB]/85 px-3 py-1 text-[10px] font-bold tracking-[0.12em] uppercase text-white">
          {stageLabel(after)}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3 text-[11px]">
        <div className="rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-2" data-testid="compare-before-label">
          <span className="block font-semibold text-slate-600">Before · {stageLabel(before)}</span>
          <span className="text-slate-400">{CATEGORY_LABELS[before.category]} · {shortDate(recordDate(before))}</span>
        </div>
        <div className="rounded-xl border border-[#2563EB]/15 bg-[#2563EB]/5 px-3 py-2 text-right" data-testid="compare-after-label">
          <span className="block font-semibold text-[#1D4ED8]">After · {stageLabel(after)}</span>
          <span className="text-slate-400">{CATEGORY_LABELS[after.category]} · {shortDate(recordDate(after))}</span>
        </div>
      </div>
    </div>
  );
}
